import { CommonModule } from '@angular/common';
import { Component, inject, signal } from '@angular/core';
import { MatTooltipModule } from '@angular/material/tooltip';
import { RouterModule } from '@angular/router';
import { ICellRendererAngularComp } from 'ag-grid-angular';
import { ICellRendererParams } from 'ag-grid-community';
import { ChipColor } from '../../../../components/pan-chip/pan-chip.component';
import { UiModule } from '../../../../components/ui.module';
import { tryGetErrorMessage } from '../../../../error.utils';
import { CaseTrackingService } from '../../../../service/case-tracking.service';
import { NotifyService } from '../../../../service/notify.service';
import { CaseIncidentTableEntry, CaseStatus } from '../../../../types';
import { getCaseStatusColor, getCaseStatusText } from '../../../../helpers';

@Component({
  selector: 'app-pan-cell-case-tracking-group',
  imports: [UiModule, RouterModule, CommonModule, MatTooltipModule],
  templateUrl: './cell-case-tracking-group.component.html',
  styleUrl: './cell-case-tracking-group.component.scss',
})
export class CellCaseTrackingGroupComponent implements ICellRendererAngularComp {
  private caseTrackingService = inject(CaseTrackingService);
  private notifyService = inject(NotifyService);

  public params!: ICellRendererParams & { hasShareHistory?: (caseId: string) => boolean };

  public caseNumber = signal<string>('');
  public locationName = signal<string>('');
  public isResolving = signal<boolean>(false);
  public firstCell = signal<CaseIncidentTableEntry | undefined>(undefined);

  agInit(params: ICellRendererParams) {
    this.params = params as typeof this.params;
    const [caseNumber, locationName] = params.value.split('@');
    this.caseNumber.set(caseNumber.trim());
    this.locationName.set(locationName.trim());
    this.firstCell.set(params.node.allLeafChildren[0].data);
  }

  refresh(params: ICellRendererParams) {
    this.params = params;
    return true;
  }

  getStatusColor(status: CaseStatus): ChipColor {
    return getCaseStatusColor(status);
  }

  getStatusText(status: CaseStatus): string {
    return getCaseStatusText(status);
  }

  toggleResolveCase() {
    this.isResolving.set(true);
    const caseData = this.firstCell();
    if (!caseData) return;

    const mutate =
      caseData.status !== CaseStatus.Resolved
        ? () => this.caseTrackingService.resolveCase(caseData.id)
        : () => this.caseTrackingService.unresolveCase(caseData.id);
    mutate().subscribe(
      (updatedCase) => {
        // response can be null if 204
        if (updatedCase) {
          this.firstCell.update((caseData) => ({
            ...caseData,
            status: updatedCase.status,
          }));
        }
        this.isResolving.set(false);
      },
      (err) => {
        this.notifyService.notify(tryGetErrorMessage(err));
        this.isResolving.set(false);
      },
    );
  }
}
